// GoalsView — agent performance targets per period (monthly/quarterly/yearly).
// Progress is computed live by the backend from existing entities (commissions,
// policies, clients, leads, premium) — no separate progress row to keep in sync.
//
// Tab structure:
//   list    → grid of goals with ring charts and current/remaining
//   editor  → create or edit a goal

import React, { useEffect, useState, useCallback } from "react";
import {
  Plus, Edit2, Trash2, Target, Trophy, AlertCircle, Loader2,
  DollarSign, FileText, Users, UserPlus, TrendingUp, X, Check,
} from "lucide-react";
import {
  listGoals, createGoal, updateGoal, deleteGoal,
} from "../api.js";
import { confirmDialog } from "./ui.jsx";

const METRIC_META = {
  COMMISSION_DOLLARS: { icon: DollarSign,  labelEs: "Comisiones $",     labelEn: "Commission $",     color: "#10b981", isMoney: true  },
  POLICIES_NEW:       { icon: FileText,    labelEs: "Pólizas nuevas",   labelEn: "New policies",     color: "#3b82f6", isMoney: false },
  CLIENTS_NEW:        { icon: Users,       labelEs: "Clientes nuevos",  labelEn: "New clients",      color: "#a855f7", isMoney: false },
  LEADS_CONVERTED:    { icon: UserPlus,    labelEs: "Leads convertidos","labelEn": "Leads converted",color: "#f59e0b", isMoney: false },
  PREMIUM_TOTAL:      { icon: TrendingUp,  labelEs: "Prima total $",    labelEn: "Premium total $",  color: "#06b6d4", isMoney: true  },
};
// Fix typo (the labelEn key needs to be a JS identifier)
METRIC_META.LEADS_CONVERTED.labelEn = "Leads converted";

const PERIOD_LABEL = {
  MONTHLY:   { es: "Mensual",     en: "Monthly" },
  QUARTERLY: { es: "Trimestral",  en: "Quarterly" },
  YEARLY:    { es: "Anual",       en: "Yearly" },
};

const POLICY_TYPES = [
  "HEALTH", "LIFE", "AUTO", "HOME", "MEDICARE_SUPPLEMENT",
  "MEDICARE_ADVANTAGE", "DENTAL", "VISION", "DISABILITY", "ANNUITY", "COMMERCIAL",
];

// ----------------------------------------------------------------------
// Main view
// ----------------------------------------------------------------------

export function GoalsView({ currentUser, lang = "en" }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | goal

  const refresh = useCallback(async () => {
    setLoading(true); setError("");
    try { setGoals(await listGoals()); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const h = (e) => { if (e?.detail?.view === "goals") setEditing(null); };
    window.addEventListener("deskte:nav-reset", h);
    return () => window.removeEventListener("deskte:nav-reset", h);
  }, []);

  if (editing) {
    return (
      <GoalEditor
        initial={editing === "new" ? null : editing}
        currentUser={currentUser}
        lang={lang}
        onCancel={() => setEditing(null)}
        onSaved={async () => { await refresh(); setEditing(null); }}
      />
    );
  }

  // Bucket goals by period for visual grouping.
  const byPeriod = { MONTHLY: [], QUARTERLY: [], YEARLY: [] };
  for (const g of goals) {
    if (g.isActive && !g.deletedAt) (byPeriod[g.period] || byPeriod.MONTHLY).push(g);
  }
  const inactive = goals.filter((g) => !g.isActive);

  return (
    <div style={{ padding: "1.5rem", maxWidth: 1200, margin: "0 auto" }}>
      <Header lang={lang} onCreate={() => setEditing("new")} />

      {error && (
        <div style={{
          background: "#3a1a1a", border: "1px solid #ef4444", borderRadius: 10,
          padding: ".75rem 1rem", marginBottom: 12, color: "#fca5a5",
          display: "flex", alignItems: "center", gap: 10, fontSize: ".88rem",
        }}>
          <AlertCircle size={16} /><span style={{ flex: 1 }}>{error}</span>
          <button onClick={() => setError("")} style={{ background: "none", border: "none", color: "#fca5a5", cursor: "pointer" }}>×</button>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: "3rem", color: "var(--c-text2,#7aa3c0)" }}>
          <Loader2 size={20} className="dkt-spin" /> {t("Cargando…", "Loading…")}
        </div>
      ) : goals.length === 0 ? (
        <EmptyState lang={lang} onCreate={() => setEditing("new")} />
      ) : (
        <>
          {byPeriod.MONTHLY.length > 0 && (
            <Section title={t("Metas mensuales", "Monthly goals")} goals={byPeriod.MONTHLY} lang={lang}
                     onEdit={setEditing} onDelete={async (g) => {
                       if (!(await confirmDialog({ message: t(`¿Eliminar la meta "${labelFor(g, lang)}"?`, `Delete goal "${labelFor(g, lang)}"?`), danger: true }))) return;
                       try { await deleteGoal(g.id); await refresh(); } catch (err) { setError(err.message); }
                     }} />
          )}
          {byPeriod.QUARTERLY.length > 0 && (
            <Section title={t("Metas trimestrales", "Quarterly goals")} goals={byPeriod.QUARTERLY} lang={lang}
                     onEdit={setEditing} onDelete={async (g) => {
                       if (!(await confirmDialog({ message: t(`¿Eliminar la meta "${labelFor(g, lang)}"?`, `Delete goal "${labelFor(g, lang)}"?`), danger: true }))) return;
                       try { await deleteGoal(g.id); await refresh(); } catch (err) { setError(err.message); }
                     }} />
          )}
          {byPeriod.YEARLY.length > 0 && (
            <Section title={t("Metas anuales", "Yearly goals")} goals={byPeriod.YEARLY} lang={lang}
                     onEdit={setEditing} onDelete={async (g) => {
                       if (!(await confirmDialog({ message: t(`¿Eliminar la meta "${labelFor(g, lang)}"?`, `Delete goal "${labelFor(g, lang)}"?`), danger: true }))) return;
                       try { await deleteGoal(g.id); await refresh(); } catch (err) { setError(err.message); }
                     }} />
          )}
          {inactive.length > 0 && (
            <details style={{ marginTop: "2rem" }}>
              <summary style={{ cursor: "pointer", color: "var(--c-text3,#45688a)", fontSize: ".82rem" }}>
                {t(`${inactive.length} meta(s) inactivas`, `${inactive.length} inactive goal(s)`)}
              </summary>
            </details>
          )}
        </>
      )}
    </div>
  );
}

function Header({ lang, onCreate }) {
  const t = (es, en) => (lang === "es" ? es : en);
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.25rem", flexWrap: "wrap", gap: ".75rem" }}>
      <div>
        <h2 style={{ margin: 0, fontSize: "1.4rem", color: "var(--c-white,#eef6ff)" }}>
          🎯 {t("Metas y desempeño", "Goals & performance")}
        </h2>
        <div style={{ fontSize: ".82rem", color: "var(--c-text2,#7aa3c0)", marginTop: 2 }}>
          {t("Define metas mensuales, trimestrales o anuales. El progreso se calcula automáticamente desde tus comisiones, pólizas y leads.",
             "Set monthly, quarterly or yearly targets. Progress is computed live from your commissions, policies and leads.")}
        </div>
      </div>
      <button onClick={onCreate} style={primaryBtn()}>
        <Plus size={16} /> {t("Nueva meta", "New goal")}
      </button>
    </div>
  );
}

function EmptyState({ lang, onCreate }) {
  const t = (es, en) => (lang === "es" ? es : en);
  return (
    <div style={{
      textAlign: "center", padding: "3rem 1rem",
      background: "var(--c-surface,#0d1c2e)", border: "1px dashed var(--c-border,#1a3550)",
      borderRadius: 16,
    }}>
      <div style={{
        width: 64, height: 64, borderRadius: 16, margin: "0 auto 1rem",
        background: "#10b98122", border: "1px solid #10b98166",
        display: "grid", placeItems: "center",
      }}>
        <Target size={28} color="#10b981" />
      </div>
      <h3 style={{ margin: "0 0 .5rem", color: "var(--c-white,#eef6ff)", fontSize: "1.1rem" }}>
        {t("Aún no tienes metas", "No goals yet")}
      </h3>
      <p style={{ color: "var(--c-text2,#7aa3c0)", fontSize: ".88rem", margin: "0 0 1.5rem", maxWidth: 460, marginInline: "auto", lineHeight: 1.55 }}>
        {t("Ejemplo: 'Cerrar $5,000 en comisiones este mes' o '10 pólizas Health en Q2'.",
           "Example: 'Close $5,000 in commissions this month' or '10 Health policies in Q2'.")}
      </p>
      <button onClick={onCreate} style={primaryBtn()}>
        <Plus size={16} /> {t("Crear la primera meta", "Create the first goal")}
      </button>
    </div>
  );
}

function Section({ title, goals, lang, onEdit, onDelete }) {
  return (
    <div style={{ marginBottom: "1.5rem" }}>
      <div style={{
        fontSize: ".75rem", letterSpacing: ".06em", textTransform: "uppercase",
        color: "var(--c-text3,#45688a)", fontWeight: 700, marginBottom: ".75rem",
      }}>
        {title}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(290px,1fr))", gap: "1rem" }}>
        {goals.map((g) => (
          <GoalCard key={g.id} goal={g} lang={lang} onEdit={() => onEdit(g)} onDelete={() => onDelete(g)} />
        ))}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// GoalCard with ring chart
// ----------------------------------------------------------------------

function GoalCard({ goal, lang, onEdit, onDelete }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const meta = METRIC_META[goal.metric] || METRIC_META.COMMISSION_DOLLARS;
  const Icon = meta.icon;
  const pct = Math.min(100, goal.progress?.pct ?? 0);
  const reached = pct >= 100;
  const target = Number(goal.target);
  const current = Number(goal.progress?.current ?? 0);

  const fmt = (n) => meta.isMoney
    ? `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    : Number(n).toLocaleString();

  return (
    <div style={{
      background: "var(--c-surface,#0d1c2e)",
      border: `1px solid ${reached ? "#10b98155" : "var(--c-border,#1a3550)"}`,
      borderRadius: 14, padding: "1.1rem",
      display: "flex", flexDirection: "column", gap: ".7rem",
      position: "relative", overflow: "hidden",
    }}>
      {reached && (
        <div style={{
          position: "absolute", top: 8, right: 8,
          background: "linear-gradient(135deg, #fbbf24, #f59e0b)",
          color: "#0a1628", padding: "3px 9px", borderRadius: 999,
          fontSize: ".68rem", fontWeight: 800, display: "inline-flex", alignItems: "center", gap: 4,
        }}>
          <Trophy size={11} /> 100%
        </div>
      )}

      <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
        {/* Ring chart */}
        <RingChart pct={pct} color={meta.color} reached={reached} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, color: meta.color, fontSize: ".72rem", fontWeight: 700, letterSpacing: ".04em", marginBottom: 3 }}>
            <Icon size={12} /> {(lang === "es" ? meta.labelEs : meta.labelEn).toUpperCase()}
          </div>
          <div style={{ fontSize: "1rem", fontWeight: 800, color: "var(--c-white,#eef6ff)", marginBottom: 2 }}>
            {goal.label || labelFor(goal, lang)}
          </div>
          <div style={{ fontSize: ".75rem", color: "var(--c-text2,#7aa3c0)" }}>
            {goal.agent
              ? <>👤 {goal.agent.displayName}</>
              : <>🌐 {t("Agencia", "Agency-shared")}</>}
          </div>
        </div>
      </div>

      <div style={{
        background: "var(--c-surface2,#112538)", borderRadius: 10, padding: ".7rem .9rem",
        display: "flex", flexDirection: "column", gap: 4,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".82rem" }}>
          <span style={{ color: "var(--c-text2,#7aa3c0)" }}>{t("Actual", "Current")}</span>
          <span style={{ color: meta.color, fontWeight: 700 }}>{fmt(current)}</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".82rem" }}>
          <span style={{ color: "var(--c-text2,#7aa3c0)" }}>{t("Meta", "Target")}</span>
          <span style={{ color: "var(--c-white,#eef6ff)", fontWeight: 700 }}>{fmt(target)}</span>
        </div>
        {!reached && (
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".82rem", paddingTop: 4, borderTop: "1px dashed var(--c-border,#1a3550)" }}>
            <span style={{ color: "var(--c-text3,#45688a)" }}>{t("Faltan", "Remaining")}</span>
            <span style={{ color: "var(--c-amber,#f59e0b)", fontWeight: 700 }}>{fmt(goal.progress?.remaining ?? 0)}</span>
          </div>
        )}
      </div>

      <div style={{ fontSize: ".7rem", color: "var(--c-text3,#45688a)", textAlign: "center" }}>
        {fmtPeriodRange(goal, lang)}
        {goal.policyType && <> · {goal.policyType}</>}
      </div>

      <div style={{ display: "flex", gap: 6 }}>
        <button onClick={onEdit} style={btn("ghost")}><Edit2 size={11} /> {t("Editar", "Edit")}</button>
        <button onClick={onDelete} style={btn("danger")}><Trash2 size={11} /></button>
      </div>
    </div>
  );
}

function RingChart({ pct, color, reached }) {
  const size = 80;
  const stroke = 8;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = c * (Math.min(100, pct) / 100);
  const ringColor = reached ? "#10b981" : color;
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--c-surface2,#112538)" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={ringColor} strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={`${dash} ${c}`}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
              style={{ transition: "stroke-dasharray .5s ease-out" }} />
      <text x={size / 2} y={size / 2 + 5} textAnchor="middle"
            fontSize="18" fontWeight="800" fill="var(--c-white,#eef6ff)">
        {Math.round(pct)}%
      </text>
    </svg>
  );
}

// ----------------------------------------------------------------------
// GoalEditor — create / edit
// ----------------------------------------------------------------------

function GoalEditor({ initial, currentUser, onCancel, onSaved, lang }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const [metric, setMetric] = useState(initial?.metric || "COMMISSION_DOLLARS");
  const [target, setTarget] = useState(initial?.target ? String(initial.target) : "");
  const [period, setPeriod] = useState(initial?.period || "MONTHLY");
  const [label, setLabel] = useState(initial?.label || "");
  const [policyType, setPolicyType] = useState(initial?.policyType || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function save() {
    const targetNum = Number(target);
    if (!Number.isFinite(targetNum) || targetNum <= 0) {
      setError(t("La meta debe ser un número positivo.", "Target must be a positive number."));
      return;
    }
    setBusy(true); setError("");
    try {
      const payload = {
        metric, target: targetNum, period,
        label: label || null,
        policyType: policyType || null,
      };
      if (initial?.id) await updateGoal(initial.id, payload);
      else await createGoal(payload);
      await onSaved();
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  const meta = METRIC_META[metric] || METRIC_META.COMMISSION_DOLLARS;
  const supportsPolicyType = metric === "COMMISSION_DOLLARS" || metric === "POLICIES_NEW" || metric === "PREMIUM_TOTAL";

  return (
    <div style={{ padding: "1.5rem", maxWidth: 720, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.25rem", flexWrap: "wrap", gap: ".5rem" }}>
        <div>
          <button onClick={onCancel} style={{ background: "none", border: "none", color: "var(--c-text2,#7aa3c0)", cursor: "pointer", fontSize: ".82rem", marginBottom: 6 }}>
            ← {t("Volver", "Back")}
          </button>
          <h2 style={{ margin: 0, fontSize: "1.4rem", color: "var(--c-white,#eef6ff)" }}>
            {initial?.id ? t("Editar meta", "Edit goal") : t("Nueva meta", "New goal")}
          </h2>
        </div>
        <button onClick={save} disabled={busy} style={primaryBtn()}>
          {busy ? <Loader2 size={14} className="dkt-spin" /> : <Check size={14} />}
          {busy ? t("Guardando…", "Saving…") : (initial?.id ? t("Guardar", "Save") : t("Crear meta", "Create goal"))}
        </button>
      </div>

      {error && (
        <div style={{
          background: "#3a1a1a", border: "1px solid #ef4444", borderRadius: 10,
          padding: ".75rem 1rem", marginBottom: 12, color: "#fca5a5",
          display: "flex", alignItems: "center", gap: 10, fontSize: ".88rem",
        }}>
          <AlertCircle size={16} /><span>{error}</span>
        </div>
      )}

      <Card>
        {/* `plain`: dentro hay botones. Ver el comentario de Field. */}
        <Field label={t("Tipo de meta", "Goal type")} plain>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 8 }}>
            {Object.entries(METRIC_META).map(([key, m]) => {
              const M = m.icon;
              const active = metric === key;
              return (
                <button key={key} onClick={() => setMetric(key)} style={{
                  padding: ".7rem", borderRadius: 10, cursor: "pointer", textAlign: "left",
                  background: active ? `${m.color}22` : "var(--c-surface2,#112538)",
                  color: active ? m.color : "var(--c-text2,#7aa3c0)",
                  border: `1px solid ${active ? m.color + "66" : "var(--c-border,#1a3550)"}`,
                  display: "flex", alignItems: "center", gap: 8, fontSize: ".82rem", fontWeight: 600,
                }}>
                  <M size={16} />
                  {lang === "es" ? m.labelEs : m.labelEn}
                </button>
              );
            })}
          </div>
        </Field>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label={t("Meta", "Target")} required>
            <div style={{ position: "relative" }}>
              {meta.isMoney && <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--c-text3,#45688a)", fontWeight: 700 }}>$</span>}
              <input type="number" min={1} value={target} onChange={(e) => setTarget(e.target.value)}
                     placeholder={meta.isMoney ? "5000" : "10"}
                     style={{ ...inputStyle(), paddingLeft: meta.isMoney ? 24 : undefined }} />
            </div>
          </Field>
          <Field label={t("Período", "Period")}>
            <select value={period} onChange={(e) => setPeriod(e.target.value)} style={inputStyle()}>
              {Object.entries(PERIOD_LABEL).map(([k, p]) => (
                <option key={k} value={k}>{lang === "es" ? p.es : p.en}</option>
              ))}
            </select>
          </Field>
        </div>

        {supportsPolicyType && (
          <Field label={t("Filtrar por tipo de póliza (opcional)", "Filter by policy type (optional)")}>
            <select value={policyType} onChange={(e) => setPolicyType(e.target.value)} style={inputStyle()}>
              <option value="">{t("— Todos los tipos —", "— All types —")}</option>
              {POLICY_TYPES.map((p) => (<option key={p} value={p}>{p}</option>))}
            </select>
          </Field>
        )}

        <Field label={t("Etiqueta (opcional)", "Label (optional)")}>
          <input value={label} onChange={(e) => setLabel(e.target.value)}
                 placeholder={t("ej. Push de mayo", "e.g. May push")} style={inputStyle()} />
        </Field>
      </Card>
    </div>
  );
}

// ----------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------

function labelFor(goal, lang) {
  const meta = METRIC_META[goal.metric] || METRIC_META.COMMISSION_DOLLARS;
  const period = PERIOD_LABEL[goal.period];
  const periodLabel = period ? (lang === "es" ? period.es : period.en).toLowerCase() : goal.period;
  return `${meta.isMoney ? "$" + Number(goal.target).toLocaleString() : Number(goal.target)} ${(lang === "es" ? meta.labelEs : meta.labelEn).toLowerCase()} ${periodLabel}`;
}

function fmtPeriodRange(goal, lang) {
  // periodStart/End are stored by the backend as midnight-UTC of the intended
  // calendar date (1st of month, etc). Render in UTC so a viewer behind UTC
  // (e.g. Florida, UTC-4) doesn't see the previous day — without timeZone:'UTC'
  // a "Jun 1" start rendered as "May 31".
  const start = new Date(goal.periodStart).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
  const end = new Date(goal.periodEnd).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
  return `${start} → ${end}`;
}

function Card({ children }) {
  return (
    <div style={{
      background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)",
      borderRadius: 12, padding: "1.25rem",
      display: "flex", flexDirection: "column", gap: ".75rem",
    }}>{children}</div>
  );
}

// `plain` cambia <label> por <div>, y hay que usarlo siempre que dentro haya un
// boton. Al pulsar en cualquier parte de un <label> que no sea contenido
// interactivo, el navegador dispara un clic sobre el PRIMER control etiquetable
// que haya dentro: en "Tipo de meta" eso significaba que tocar el titulo
// cambiaba la meta a la primera opcion, sin que nadie pulsara nada. Mismo fallo
// que el pop up de IA en Secuencias, encontrado por la misma prueba. Ver E-121.
function Field({ label, required, children, plain = false }) {
  const Wrap = plain ? "div" : "label";
  return (
    <Wrap style={{ display: "block" }}>
      <div style={{ fontSize: ".75rem", color: "var(--c-text2,#7aa3c0)", marginBottom: 4, fontWeight: 600 }}>
        {label}{required ? <span style={{ color: "#ef4444", marginLeft: 3 }}>*</span> : null}
      </div>
      {children}
    </Wrap>
  );
}

function inputStyle() {
  return {
    width: "100%", padding: ".55rem .75rem", borderRadius: 8,
    background: "var(--c-surface2,#112538)", color: "var(--c-white,#eef6ff)",
    border: "1px solid var(--c-border,#1a3550)", fontSize: ".88rem", outline: "none",
  };
}

function primaryBtn() {
  return {
    padding: ".55rem 1.25rem", borderRadius: 9,
    background: "linear-gradient(135deg, #10b981, #059669)", color: "#fff",
    border: "none", cursor: "pointer", fontWeight: 700, fontSize: ".82rem",
    display: "inline-flex", alignItems: "center", gap: 6,
  };
}

function btn(kind) {
  const map = {
    primary: { bg: "#10b98122", color: "#10b981", border: "#10b98166" },
    ghost:   { bg: "var(--c-surface2,#112538)", color: "var(--c-text2,#7aa3c0)", border: "var(--c-border,#1a3550)" },
    danger:  { bg: "#7f1d1d22", color: "#fca5a5", border: "#ef444466" },
  }[kind];
  return {
    background: map.bg, color: map.color, border: `1px solid ${map.border}`,
    padding: ".4rem .65rem", borderRadius: 8, cursor: "pointer", fontSize: ".75rem",
    display: "inline-flex", alignItems: "center", gap: 5, fontWeight: 600,
    flex: kind === "ghost" ? 1 : undefined,
  };
}
